import type { MachineId, Material, TokenSpec } from '../../shared/contract.js';
import { hasTag, makeRuntime } from '../token.js';
import { v3 } from '../vec.js';
import { Cabinet, type FieldSolid, type TagTable } from './base.js';

/**
 * EINARMIGER BANDIT. Three physical reel drums whose symbols are windows onto tokens
 * drawn from Der Beutel — your deck IS the reel strip (machines.md's strongest
 * intertwining decision).
 *
 * DECISION: the drums are integrated here as 1-DOF kinematic bodies (angle, angular
 * velocity, detents) rather than as Rapier rigid bodies. A constrained single-axis spin
 * gains nothing from a 3D solver, and keeping three drums out of the physics world is
 * most of the reason the 300-token pile still fits in the tick budget. They are still
 * genuinely physical: spin-down, detents, skill-stop timing and slip are all simulated.
 *
 * This is the one cabinet with a rich tag table, because it has an odds-space beyond
 * physics. The table is finite, declared, and printed on the help card.
 */

export interface BanditSetzen {
  /** lever pull strength 0..1 — affects spin time */
  power: number;
  /** how many tokens are staked this pull */
  stake: number;
}

const SLOTS = 8;
const DETENT = (Math.PI * 2) / SLOTS;
const SETTLE_SECONDS = 0.35;
const MATERIALS: readonly Material[] = ['kupfer', 'silber', 'gold', 'obsidian'];

type ReelState = 'idle' | 'spinning' | 'settling' | 'stopped';

interface Reel {
  slots: TokenSpec[];
  theta: number;
  omega: number;
  state: ReelState;
  /** tick at which it stops itself if the player never skill-stops it */
  autoStopTick: number;
  settleFrom: number;
  settleTo: number;
  settleT: number;
  /** half-detent stop ([Geist]) shows two symbols at once */
  halfStop: boolean;
  held: boolean;
}

export class Bandit extends Cabinet<BanditSetzen> {
  readonly id: MachineId = 'bandit';
  readonly limitLabel = 'Trefferwert-Grenze — darüber springen die Walzen von den Achsen.';
  readonly breakMode = 'axle' as const;

  /** The declared odds mapping, straight out of machines.md. Data. */
  readonly tagTable: TagTable = {
    Magnet: { effect: 'copyNeighbours', note: 'Nachbarwalzen werden zu diesem Symbol hingezogen.' },
    Spalt: { effect: 'countsTwice', note: 'Dieses Symbol zählt bei Treffern doppelt.' },
    Geist: { effect: 'betweenDetents', note: 'Darf zwischen zwei Rasten halten (halbe Symbole).' },
    Fluch: { effect: 'invertOdds', note: 'Dreht Treffer- und Nietenchance dieses Zuges um.' },
    Kleber: { effect: 'holdNextSpin', note: 'Symbol bleibt beim nächsten Zug stehen (Gratis-Halt).' },
  };

  private reels: Reel[] = [];
  private spinning = false;
  private stake = 1;
  private lastMatchValue = 0;
  private lastResult = 'noch kein Zug';
  private matchCap = 900;
  private spins = 0;
  private wins = 0;

  build(): void {
    for (let i = 0; i < 3; i++) this.reels.push(this.freshReel());
  }

  private freshReel(): Reel {
    return {
      slots: [],
      theta: 0,
      omega: 0,
      state: 'idle',
      autoStopTick: 0,
      settleFrom: 0,
      settleTo: 0,
      settleT: 0,
      halfStop: false,
      held: false,
    };
  }

  fieldSolids(): readonly FieldSolid[] {
    return [];
  }

  /** Verb 1: Setzen — stake choice plus lever pull strength. */
  setzen(p: BanditSetzen): void {
    if (this.broken || this.spinning) return;
    this.stake = Math.max(1, Math.round(p.stake));
    const power = clamp(p.power, 0, 1);
    this.spins++;

    for (let i = 0; i < this.reels.length; i++) {
      const r = this.reels[i]!;
      // [Kleber] holdNextSpin: a stuck symbol keeps its strip position — a free hold
      if (!r.held || r.slots.length !== SLOTS) {
        r.slots = [];
        for (let s = 0; s < SLOTS; s++) r.slots.push(this.host.drawFromBeutel('reels'));
      }
      r.held = false;
      r.omega = (18 + 14 * power) * (1 + 0.08 * i) + this.host.rng.range('reels:spin', -0.6, 0.6);
      r.state = 'spinning';
      r.halfStop = false;
      const secs = 1.6 + 0.55 * i + power * 1.2;
      r.autoStopTick = this.host.tick + Math.round(secs / this.host.dt);
    }
    this.spinning = true;
  }

  /** Verb 2 on this cabinet: Rütteln IS the skill-stop (pachislo detents). */
  protected override onShove(): void {
    for (const r of this.reels) {
      if (r.state === 'spinning') {
        this.beginSettle(r);
        return;
      }
    }
  }

  protected escalate(level: number): void {
    // machines.md: Nachlegen adds a 4th/5th reel — bigger matches, worse odds
    if (this.reels.length < 5) {
      const r = this.freshReel();
      for (let s = 0; s < SLOTS; s++) r.slots.push(this.host.drawFromBeutel('reels'));
      r.state = 'stopped';
      this.reels.push(r);
    }
    this.matchCap = Math.max(300, this.matchCap - 150 * (level - 1));
  }

  tickMachine(dt: number): void {
    this.tickShared(dt);
    if (this.broken || !this.spinning) return;

    for (const r of this.reels) {
      if (r.state === 'spinning') {
        r.omega = Math.max(0, r.omega - 6.5 * dt);
        r.theta = wrap(r.theta + r.omega * dt);
        if (r.omega <= 2.5 || this.host.tick >= r.autoStopTick) this.beginSettle(r);
      } else if (r.state === 'settling') {
        r.settleT += dt / SETTLE_SECONDS;
        if (r.settleT >= 1) {
          r.theta = wrap(r.settleTo);
          r.state = 'stopped';
          this.host.emit({
            t: 'reelStop',
            reel: this.reels.indexOf(r),
            symbolToken: this.windowSpecs(r)[0]?.id ?? -1,
          });
        } else {
          const e = ease(r.settleT);
          r.theta = wrap(r.settleFrom + (r.settleTo - r.settleFrom) * e);
        }
      }
    }

    if (this.reels.every((r) => r.state === 'stopped')) {
      this.spinning = false;
      this.resolve();
    }
    this.checkLimit();
  }

  private beginSettle(r: Reel): void {
    const idx = this.reels.indexOf(r);
    const stillRunning = this.reels.filter((x) => x.state === 'spinning').length;

    let target = Math.round(r.theta / DETENT) * DETENT;
    if (target < r.theta) target += DETENT; // always settle forwards

    // the landing symbol decides whether this drum may rest between detents
    const landing = r.slots[slotOf(target)];
    if (landing && hasTag(landing, 'Geist') && this.tagTable.Geist?.effect === 'betweenDetents') {
      target += DETENT / 2;
      r.halfStop = true;
    }

    // last reel to stop: the classic slip. One declared, bendable, invertible roll.
    if (stillRunning <= 1) {
      const cursedSpec = this.windowTokenWith('Fluch');
      const cursed = cursedSpec ? makeRuntime(cursedSpec, this.id) : null;
      const hold = this.host.roll('reels', 'zug', 0.18, cursed);
      if (!hold) target += DETENT;
    }

    // [Magnet]: neighbouring reels are drawn toward copying this symbol
    // [Magnet]: a magnet symbol already showing on a *neighbouring* drum drags this one
    // toward copying it. Neighbour, not self — otherwise a magnet just copies itself.
    const magnetSpec = this.neighbourMagnet(idx);
    if (magnetSpec && this.tagTable.Magnet?.effect === 'copyNeighbours') {
      const pulled = this.pullToward(r, target, magnetSpec.material);
      if (pulled !== null) target = pulled;
    }

    r.settleFrom = r.theta;
    r.settleTo = target;
    r.settleT = 0;
    r.state = 'settling';
    r.omega = 0;
  }

  /** Move a drum's target by at most two detents to show `want`, if it can. */
  private pullToward(r: Reel, target: number, want: Material): number | null {
    for (let d = 1; d <= 2; d++) {
      for (const s of [d, -d]) {
        const cand = target + s * DETENT;
        const spec = r.slots[slotOf(cand)];
        if (spec && spec.material === want) return cand;
      }
    }
    return null;
  }

  /** A magnet symbol resting in an adjacent drum's window. */
  private neighbourMagnet(idx: number): TokenSpec | null {
    for (const d of [-1, 1]) {
      const n = this.reels[idx + d];
      if (!n || n.state === 'spinning') continue;
      for (const s of this.windowSpecs(n)) if (hasTag(s, 'Magnet')) return s;
    }
    return null;
  }

  private windowTokenWith(tag: 'Magnet' | 'Fluch'): TokenSpec | null {
    for (const r of this.reels) {
      for (const s of this.windowSpecs(r)) {
        if (hasTag(s, tag)) return s;
      }
    }
    return null;
  }

  /** What the glass shows for one drum: one symbol, or two when it stopped half-way. */
  private windowSpecs(r: Reel): TokenSpec[] {
    if (r.slots.length === 0) return [];
    const i = slotOf(r.theta);
    if (!r.halfStop) return [r.slots[i]!];
    const j = (i + 1) % SLOTS;
    return [r.slots[i]!, r.slots[j]!];
  }

  private resolve(): void {
    const specs: TokenSpec[] = [];
    for (const r of this.reels) specs.push(...this.windowSpecs(r));
    if (specs.length === 0) return;

    // count by material; [Spalt] symbols count twice (declared table entry)
    const counts = new Map<Material, number>();
    for (const s of specs) {
      const weight = hasTag(s, 'Spalt') && this.tagTable.Spalt?.effect === 'countsTwice' ? 2 : 1;
      counts.set(s.material, (counts.get(s.material) ?? 0) + weight);
    }
    let best: Material = 'kupfer';
    let bestN = 0;
    for (const m of MATERIALS) {
      const n = counts.get(m) ?? 0;
      if (n > bestN) {
        bestN = n;
        best = m;
      }
    }

    const matched = specs.filter((s) => s.material === best);
    const at = this.w(v3(0, 1.5, 0));

    if (bestN >= 3) {
      const base = matched.reduce((a, s) => a + s.faceValue, 0) * 4 * this.stake;
      const jackpot = bestN >= 4;
      const tok = this.host.spawnToken(matched[0]!, this.id, at, v3(0, 0, 0), false);
      this.lastMatchValue = base;
      this.lastResult = `${bestN}× ${best}`;
      this.wins++;
      this.registerScore(tok, jackpot ? 'walzen-jackpot' : 'walzen', base, at, jackpot);
      this.host.removeToken(tok.id, 'scored');
    } else if (bestN === 2) {
      this.lastResult = `Fast: 2× ${best}`;
      this.feedTopf(2 * this.stake);
      this.registerDud(null);
    } else {
      this.lastResult = 'Niete';
      this.registerDud(null);
    }

    // [Kleber] holdNextSpin: mark drums whose window symbol sticks
    for (const r of this.reels) {
      if (this.windowSpecs(r).some((s) => hasTag(s, 'Kleber'))) r.held = true;
    }
  }

  structuralLoad(): number {
    return this.lastMatchValue / this.matchCap;
  }

  burstValue(): number {
    return this.topf + this.lastMatchValue * 2 + 60;
  }

  extra(): unknown {
    return {
      reels: this.reels.map((r) => ({
        theta: r.theta,
        state: r.state,
        halfStop: r.halfStop,
        held: r.held,
        window: this.windowSpecs(r).map((s) => ({
          id: s.id,
          material: s.material,
          faceValue: s.faceValue,
          tags: s.tags,
        })),
      })),
      spinning: this.spinning,
      stake: this.stake,
      lastResult: this.lastResult,
      lastMatchValue: this.lastMatchValue,
      matchCap: this.matchCap,
      spins: this.spins,
      wins: this.wins,
      helpCard: Object.entries(this.tagTable).map(([tag, rule]) => `[${tag}] ${rule.note}`),
    };
  }

  /** test/demo affordance: is a pull still running? */
  get busy(): boolean {
    return this.spinning;
  }
}

const wrap = (t: number): number => {
  const two = Math.PI * 2;
  let x = t % two;
  if (x < 0) x += two;
  return x;
};
const slotOf = (theta: number): number => ((Math.round(wrap(theta) / DETENT) % SLOTS) + SLOTS) % SLOTS;
const ease = (t: number): number => 1 - Math.pow(1 - Math.min(1, t), 3);
const clamp = (x: number, lo: number, hi: number): number => (x < lo ? lo : x > hi ? hi : x);
